import { computed, onUnmounted, ref } from 'vue';

import { storeToRefs } from 'pinia';

import { getNotificationCategory } from '@/services/notificationSettingsService';
import { webSocketService } from '@/services/websocket';
import { useNotificationStore } from '@/stores/notifications';

/**
 * Composable for accessing notification functionality in components
 *
 * @param {Object} options - Configuration options
 * @param {string[]} options.types - Filter notifications by these types (empty = all)
 * @param {string[]} options.categories - Filter notifications by these categories (empty = all)
 * @returns {Object} Notification utilities and reactive state
 */
export function useNotifications(options = {}) {
  const store = useNotificationStore();
  const { types = [], categories = [] } = options;

  // Get reactive references from store
  const { notifications: allNotifications, unreadCount, connectionState, preferences, isDrawerOpen, hasNewNotification } = storeToRefs(store);

  // Filtered notifications for this component
  const notifications = computed(() => {
    let filtered = allNotifications.value;

    // Filter by specific types
    if (types.length > 0) {
      filtered = filtered.filter((n) => types.includes(n.type));
    }

    // Filter by categories
    if (categories.length > 0) {
      filtered = filtered.filter((n) => {
        const category = getNotificationCategory(n.type);
        return categories.includes(category);
      });
    }

    return filtered;
  });

  // Filtered unread count
  const filteredUnreadCount = computed(() => {
    return notifications.value.filter((n) => !n.read).length;
  });

  // Store type-specific subscription cleanups
  const subscriptions = ref([]);

  /**
   * Subscribe to specific notification types
   * Callback is invoked when a matching notification arrives
   *
   * @param {string|string[]} notificationTypes - Type(s) to subscribe to
   * @param {Function} callback - Called with notification when it arrives
   * @returns {Function} Cleanup function
   */
  function onNotification(notificationTypes, callback) {
    const typesArray = Array.isArray(notificationTypes) ? notificationTypes : [notificationTypes];

    const unsubscribes = typesArray.map((type) => {
      return webSocketService.subscribe(type, callback);
    });

    // Store for cleanup
    subscriptions.value.push(...unsubscribes);

    // Return combined cleanup function
    return () => {
      unsubscribes.forEach((unsub) => unsub());
      subscriptions.value = subscriptions.value.filter((sub) => !unsubscribes.includes(sub));
    };
  }

  /**
   * Subscribe to notifications by category
   *
   * @param {string|string[]} notificationCategories - Category(categories) to subscribe to
   * @param {Function} callback - Called with notification when it arrives
   * @returns {Function} Cleanup function
   */
  function onCategoryNotification(notificationCategories, callback) {
    const categoriesArray = Array.isArray(notificationCategories)
      ? notificationCategories
      : [notificationCategories];

    // Subscribe to all notifications and filter by category
    const unsubscribe = webSocketService.subscribe('*', (notification) => {
      const category = getNotificationCategory(notification.type);
      if (categoriesArray.includes(category)) {
        callback(notification);
      }
    });

    subscriptions.value.push(unsubscribe);

    return () => {
      unsubscribe();
      subscriptions.value = subscriptions.value.filter((sub) => sub !== unsubscribe);
    };
  }

  // Cleanup all subscriptions when component unmounts
  onUnmounted(() => {
    subscriptions.value.forEach((unsub) => unsub());
    subscriptions.value = [];
  });

  return {
    // Reactive state
    notifications,
    allNotifications,
    unreadCount,
    filteredUnreadCount,
    connectionState,
    preferences,
    isDrawerOpen,
    hasNewNotification,

    // Store actions
    markAsRead: store.markAsRead,
    markAllAsRead: store.markAllAsRead,
    removeNotification: store.removeNotification,
    clearAll: store.clearAll,
    openDrawer: store.openDrawer,
    closeDrawer: store.closeDrawer,
    toggleDrawer: store.toggleDrawer,
    updatePreferences: store.updatePreferences,

    // Subscription methods
    onNotification,
    onCategoryNotification,
  };
}

/**
 * Format a timestamp to relative time
 * @param {string|Date} timestamp
 * @returns {string} Relative time string
 */
export function formatRelativeTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'Just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  } else if (diffDay === 1) {
    return 'Yesterday';
  } else if (diffDay < 7) {
    return `${diffDay} days ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Get severity color class
 * @param {string} severity
 * @returns {string} CSS class
 */
export function getSeverityClass(severity) {
  const severityClasses = {
    info: 'notification-info',
    success: 'notification-success',
    warning: 'notification-warning',
    error: 'notification-error',
  };
  return severityClasses[severity] || severityClasses.info;
}

/**
 * Get severity icon name
 * @param {string} severity
 * @returns {string} Icon name
 */
export function getSeverityIcon(severity) {
  const severityIcons = {
    info: 'info-circle',
    success: 'check-circle',
    warning: 'exclamation-triangle',
    error: 'times-circle',
  };
  return severityIcons[severity] || severityIcons.info;
}
